<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import inboxesAPI from 'dashboard/api/inboxes';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import EvolutionStoryDialog from 'dashboard/components/EvolutionStoryDialog.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { isAdmin } = useAdmin();

const isFetchingProfile = ref(true);
const isSavingProfile = ref(false);
const isTogglingGroups = ref(false);
const groupsEnabled = ref(!!props.inbox.evolution_groups_enabled);
const isTogglingNumberChange = ref(false);
const numberChangeAllowed = ref(!!props.inbox.evolution_number_change_allowed);
const name = ref('');
const status = ref('');
const pictureUrl = ref('');
const currentPicture = ref('');
const storyDialogRef = ref(null);

onMounted(async () => {
  // The endpoint is admin-only (InboxPolicy#evolution_profile?) - skip the doomed request
  // for agents instead of letting it 403.
  if (!isAdmin.value) {
    isFetchingProfile.value = false;
    return;
  }

  try {
    const { data } = await inboxesAPI.getEvolutionProfile(props.inbox.id);
    name.value = data.name || '';
    status.value = data.status || '';
    currentPicture.value = data.picture || '';
  } catch (error) {
    // Best-effort prefill - the form below still works to set new values even if this fails.
  } finally {
    isFetchingProfile.value = false;
  }
});

const handleGroupsToggle = async newValue => {
  if (isTogglingGroups.value) return;
  const previousValue = groupsEnabled.value;
  groupsEnabled.value = newValue;
  isTogglingGroups.value = true;
  try {
    await inboxesAPI.setEvolutionGroupsEnabled(props.inbox.id, newValue);
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    groupsEnabled.value = previousValue;
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isTogglingGroups.value = false;
  }
};

const handleNumberChangeToggle = async newValue => {
  if (isTogglingNumberChange.value) return;
  const previousValue = numberChangeAllowed.value;
  numberChangeAllowed.value = newValue;
  isTogglingNumberChange.value = true;
  try {
    await inboxesAPI.setEvolutionNumberChangeAllowed(props.inbox.id, newValue);
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    numberChangeAllowed.value = previousValue;
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isTogglingNumberChange.value = false;
  }
};

const saveProfile = async () => {
  isSavingProfile.value = true;
  try {
    await inboxesAPI.updateEvolutionProfile(props.inbox.id, {
      name: name.value,
      status: status.value,
      pictureUrl: pictureUrl.value,
    });
    if (pictureUrl.value) currentPicture.value = pictureUrl.value;
    pictureUrl.value = '';
    useAlert(t('INBOX_MGMT.EVOLUTION_PROFILE.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EVOLUTION_PROFILE.SAVE_ERROR'));
  } finally {
    isSavingProfile.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-8 mx-6 max-w-4xl">
    <div
      class="relative"
      :class="{ 'pointer-events-none opacity-60': isTogglingGroups }"
    >
      <SettingsToggleSection
        :model-value="groupsEnabled"
        :header="t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.LABEL')"
        :description="t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.DESCRIPTION')"
        @update:model-value="handleGroupsToggle"
      />
    </div>

    <div
      class="relative"
      :class="{ 'pointer-events-none opacity-60': isTogglingNumberChange }"
    >
      <SettingsToggleSection
        :model-value="numberChangeAllowed"
        :header="t('INBOX_MGMT.EVOLUTION_PROFILE.NUMBER_CHANGE.LABEL')"
        :description="
          t('INBOX_MGMT.EVOLUTION_PROFILE.NUMBER_CHANGE.DESCRIPTION')
        "
        @update:model-value="handleNumberChangeToggle"
      />
    </div>

    <div class="flex flex-col gap-4 pt-6 border-t border-n-weak">
      <div>
        <span class="text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.EVOLUTION_PROFILE.TITLE') }}
        </span>
        <p class="mt-1 text-body-main text-n-slate-11">
          {{ t('INBOX_MGMT.EVOLUTION_PROFILE.DESCRIPTION') }}
        </p>
      </div>

      <div v-if="currentPicture" class="flex items-center gap-3">
        <img
          :src="currentPicture"
          class="object-cover rounded-full size-16"
          alt=""
        />
      </div>

      <SettingsFieldSection
        :label="t('INBOX_MGMT.EVOLUTION_PROFILE.NAME.LABEL')"
      >
        <NextInput
          v-model="name"
          :placeholder="t('INBOX_MGMT.EVOLUTION_PROFILE.NAME.PLACEHOLDER')"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="t('INBOX_MGMT.EVOLUTION_PROFILE.STATUS.LABEL')"
      >
        <NextInput
          v-model="status"
          :placeholder="t('INBOX_MGMT.EVOLUTION_PROFILE.STATUS.PLACEHOLDER')"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="t('INBOX_MGMT.EVOLUTION_PROFILE.PICTURE.LABEL')"
        :help-text="t('INBOX_MGMT.EVOLUTION_PROFILE.PICTURE.HELP_TEXT')"
      >
        <NextInput
          v-model="pictureUrl"
          :placeholder="t('INBOX_MGMT.EVOLUTION_PROFILE.PICTURE.PLACEHOLDER')"
        />
      </SettingsFieldSection>

      <div class="flex justify-end">
        <NextButton
          :is-loading="isSavingProfile"
          :label="t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          @click="saveProfile"
        />
      </div>
    </div>

    <div class="flex items-center justify-between pt-6 border-t border-n-weak">
      <div>
        <span class="text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.TITLE') }}
        </span>
        <p class="mt-1 text-body-main text-n-slate-11">
          {{ t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        icon="i-lucide-circle-plus"
        :label="t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.OPEN_BUTTON')"
        @click="storyDialogRef?.open()"
      />
    </div>

    <EvolutionStoryDialog ref="storyDialogRef" :inbox-id="inbox.id" />
  </div>
</template>
